import { useMemo } from 'react';

// Extra filters + top widgets for the Leads page.
// Works only on the leads array already loaded by Leads.jsx (no backend change).

const SOURCE_LABEL = { whatsapp: 'Get Quote', website: 'Website Order', agent: 'Agent Order', ads: 'Meta Ad' };
const RANGES = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
];

export const EMPTY_EXTRA = { agent: '', product: '', source: '', range: 'all' };

const createdOf = (l) => new Date(l.created_at || l.createdAt || 0);

const agentName = (agents, id) => {
  if (!id) return null;
  const a = (agents || []).find((x) => String(x.id) === String(id));
  return a ? (a.name || a.agent_name || a.email || 'Agent ' + id) : 'Agent ' + id;
};

export function applyExtraFilters(leads, extra) {
  const now = new Date();
  return leads.filter((l) => {
    if (extra.agent === 'none') { if (l.assigned_agent_id) return false; }
    else if (extra.agent && String(l.assigned_agent_id) !== String(extra.agent)) return false;

    if (extra.product && (l.product_tag || '') !== extra.product) return false;
    if (extra.source && (l.source || '') !== extra.source) return false;

    if (extra.range !== 'all') {
      const d = createdOf(l);
      if (!d.getTime()) return false;
      if (extra.range === 'today') { if (d.toDateString() !== now.toDateString()) return false; }
      else {
        const days = extra.range === '7d' ? 7 : 30;
        if (now - d > days * 86400000) return false;
      }
    }
    return true;
  });
}

function tally(leads, keyFn) {
  const m = new Map();
  leads.forEach((l) => {
    const k = keyFn(l);
    if (k == null || k === '') return;
    const e = m.get(k) || { key: k, total: 0, converted: 0 };
    e.total += 1;
    if (l.status === 'converted') e.converted += 1;
    m.set(k, e);
  });
  return [...m.values()].sort((a, b) => b.total - a.total);
}

const sel = { padding: '6px 10px', border: '1px solid #eee', borderRadius: 8, fontSize: 12, outline: 'none', background: '#fff', fontFamily: 'inherit' };

export function LeadFilterBar({ leads, agents, extra, setExtra }) {
  const products = useMemo(() => [...new Set(leads.map((l) => l.product_tag).filter(Boolean))], [leads]);
  const sources = useMemo(() => [...new Set(leads.map((l) => l.source).filter(Boolean))], [leads]);
  const set = (k, v) => setExtra({ ...extra, [k]: v });
  const active = extra.agent || extra.product || extra.source || extra.range !== 'all';

  return (
    <div style={{ background: '#fff', padding: '10px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
      <select style={sel} value={extra.agent} onChange={(e) => set('agent', e.target.value)}>
        <option value="">Agent: All</option>
        <option value="none">Unassigned</option>
        {(agents || []).map((a) => <option key={a.id} value={a.id}>{a.name || a.agent_name || a.email}</option>)}
      </select>
      <select style={sel} value={extra.product} onChange={(e) => set('product', e.target.value)}>
        <option value="">Product: All</option>
        {products.map((p) => <option key={p} value={p}>{p}</option>)}
      </select>
      <select style={sel} value={extra.source} onChange={(e) => set('source', e.target.value)}>
        <option value="">Source: All</option>
        {sources.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] || s}</option>)}
      </select>
      <select style={sel} value={extra.range} onChange={(e) => set('range', e.target.value)}>
        {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      {active && (
        <button onClick={() => setExtra(EMPTY_EXTRA)} style={{ ...sel, cursor: 'pointer', color: '#c62828', borderColor: '#f5c6c6' }}>
          Clear filters
        </button>
      )}
    </div>
  );
}

function WidgetCard({ title, rows, field, extra, setExtra, labelOf }) {
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 10 }}>{title}</div>
      {rows.length === 0 && <div style={{ fontSize: 12, color: '#bbb' }}>No data</div>}
      {rows.slice(0, 5).map((r) => {
        const on = String(extra[field]) === String(r.key);
        return (
          <div key={r.key} onClick={() => setExtra({ ...extra, [field]: on ? '' : r.key })}
            style={{ cursor: 'pointer', marginBottom: 8, opacity: extra[field] && !on ? 0.45 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#444', marginBottom: 3 }}>
              <span style={{ fontWeight: on ? 700 : 500 }}>{labelOf(r.key)}</span>
              <span style={{ color: '#999' }}>{r.total}{r.converted ? ' · ' + r.converted + ' converted' : ''}</span>
            </div>
            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: (r.total / max) * 100 + '%', height: '100%', background: on ? '#075e54' : '#25d366', borderRadius: 3 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LeadWidgets({ leads, agents, extra, setExtra }) {
  const byAgent = useMemo(() => tally(leads, (l) => l.assigned_agent_id), [leads]);
  const byProduct = useMemo(() => tally(leads, (l) => l.product_tag), [leads]);
  const bySource = useMemo(() => tally(leads, (l) => l.source), [leads]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, padding: '12px 20px', background: '#fafafa' }}>
      <WidgetCard title="Top agents" rows={byAgent} field="agent" extra={extra} setExtra={setExtra}
        labelOf={(id) => agentName(agents, id) || 'Unassigned'} />
      <WidgetCard title="Top products" rows={byProduct} field="product" extra={extra} setExtra={setExtra}
        labelOf={(p) => p} />
      <WidgetCard title="Leads by source" rows={bySource} field="source" extra={extra} setExtra={setExtra}
        labelOf={(s) => SOURCE_LABEL[s] || s} />
    </div>
  );
}
